// src/annihilation/beings.js
// ============================================================
//  ANNIHILATION — the being registry + the seven-slot skill bar.
//  Source of truth: ANNIHILATION_BUILD_GUIDE (PRIME) Parts 3–4,
//  IE_LORD_NAMES_CANON (Lord names — locked, append-only).
//
//  ONE SOURCE OF TRUTH: every system that needs "what is a
//  Spectral Tiger" (skill trees, sprites, dialogue, character
//  create, combat) reads THIS file. No per-system being lists.
//
//  Vyren pattern: being ids (spectral_tiger, chaos_dragon, z4z,
//  xulan) match stats.js's BEING_BASE_STATS keys and NEVER
//  change. Lord names, lineage labels, ability names are display
//  strings and may be retuned freely.
//
//  Primera and Impera are DISTINCT SLOTS in Annihilation (slots
//  2 and 3 of the bar) — PRIME's ability ladder, which is canon
//  here over any cross-game doc that treats them as id/display
//  of one concept.
//
//  ⚑⚑ BATCH GATE-1 (caP'n, Jul 25 2026) ⚑⚑
//  · `SKILL_SLOTS[].firstAvailable` is DELETED. Tier gating is
//    dead entirely (ANNIHILATION_GATING_DOCTRINE Part 2) — no
//    slot is withheld because of a number next to your name.
//    What a slot costs to reach now lives on the node that
//    unlocks it (`aenergyReq`) and on the ability's own
//    activation cost, read by ONE gate in treeEngine.
//    ⚑ CONSUMER: `AnnihilationCodex.jsx` still reads this field
//    in seven places (its "first available" column). That column
//    is now describing a system that does not exist and needs a
//    Codex pass — flagged roadmap §4.37.
//  · THE SMALL NUMBER LAW (Doctrine Part 10) applies to every
//    stat-magnitude number in this file. Scaled Fury's "+50% Lyf"
//    is fixed below; the rest of the sweep runs with the Tomes/
//    cut session (§4.35). Chance, pool fractions, and spatial/
//    temporal percentages STAY — they were never the problem.
// ============================================================

// (No stats import needed since GATE-1 — the tier constants were
// only ever here to power `firstAvailable`, and tier gating is
// dead. Ilk/Class below are the traits that matter now.)

// ------------------------------------------------------------
// THE SEVEN-SLOT BAR — shared vocabulary across the whole game.
// Every being has the same HUD; what fills the slots differs.
// The bar is a visible progress bar of the character's journey.
// ------------------------------------------------------------
export const SKILL_SLOTS = [
  {
    slot: 1,
    id: "lyt",
    name: "Lyt Attack",
    cooldown: "none", // paced by the Lyt stamina pool, not a timer
  },
  {
    slot: 2,
    id: "primera",
    name: "Primera",
    cooldownSec: [2, 4],
    // GATE-2 will mark this `passive: true` and move it to the
    // Passive Strip. Slot 2 stays Primera's slot forever — it
    // simply renders as a worn state rather than a verb.
  },
  {
    slot: 3,
    id: "impera",
    name: "Impera",
    cooldownSec: [6, 10],
  },
  {
    slot: 4,
    id: "uhlti",
    name: "U'hlti",
    cooldownSec: [20, 40],
  },
  {
    slot: 5,
    id: "transformation",
    name: "Transformation",
    restGated: true, // one equipped at a time
  },
  {
    slot: 6,
    id: "kings_grace",
    name: "King's Grace",
    restGated: true,
  },
  {
    slot: 7,
    id: "emperium",
    name: "Emperium",
    restGated: true, // once per village visit
  },
];

// ⚑ THE PAIRING RULE IS DEAD IN CANON (Doctrine Part 7). Arts
// transfer freely between beings that share BOTH Ilk and Class;
// missing either trait is not a block, it is the beginning of
// Heresy. The replacement is `abilityLegality(being, art)` →
// "natural" | "heretical", and it lands in BATCH GATE-2 — the
// two functions answer different questions (boolean veto vs.
// route), so callers cannot be swapped mechanically (§4.30).
// Left standing here, unchanged, so nothing breaks in between.
export const isLegalAbilityPair = (being, primeraId, imperaId) => {
  const line = (being?.lines || []).find((l) => l.primera.id === primeraId);
  return Boolean(line && line.impera.id === imperaId);
};

// ------------------------------------------------------------
// THE FOUR LAUNCH BEINGS — one per Ilk. Lord names locked in
// IE_LORD_NAMES_CANON. Lord name appears in dungeon banners,
// Lord-tier character sheets, lore dialogue; the lineage label
// stays in mechanical contexts (trees, stat blocks).
// ------------------------------------------------------------
export const BEINGS = {
  spectral_tiger: {
    id: "spectral_tiger",
    lineage: "Spectral Tiger",
    lordName: "Sael'esh",
    ilk: "Phantom", // Beast (Ghost)
    beingClass: "Beast",
    archetype: "Assassin",
    oneLiner: "Ignore defenses, hunt the squishy.",
    epigraph:
      "It walks between two worlds. On your side of the veil, you only see its claws.",
    lytAttack: {
      id: "phantom_strike",
      name: "Phantom Strike",
      effect:
        "A two-hit claw combo. First swipe deals damage +5; second swipe briefly phases the Tiger, granting iframes on the follow-through.",
      hits: 2,
      // ⚑ FOLDED IN by THE CUT (Power Doctrine): st_l1 "Keen
      // Claws" was +5 flat on the first swipe and nothing else —
      // the textbook DIAL-IS-NOT-A-NODE. The power stays, the
      // button is gone, and a scarce permanent point is freed.
      firstSwipeBonus: 5,
    },
    lines: [
      {
        id: "haunting_path",
        name: "The Haunting Path",
        primera: {
          id: "spectral_paws",
          name: "Spectral Paws",
          effect:
            "Next Lyt attack ignores enemy defenses and targets the lowest-Anima enemy in range.",
        },
        impera: {
          id: "spectral_claws",
          name: "Spectral Claws",
          effect:
            "Unleash three phantom claws that seek the three lowest-Anima enemies. Each claw deals bonus damage proportional to its target's missing Anima.",
          claws: 3,
        },
        uhlti: {
          id: "haunting_presence",
          name: "Haunting Presence",
          effect:
            "All enemies on screen take simultaneous shadow damage; all elites bleed for 5 seconds.",
          bleedDurationSec: 5,
        },
      },
    ],
    transformation: {
      id: "ghost_form",
      name: "Ghost Form",
      effect:
        "Fully incorporeal. Most enemy attacks pass through. Movement speed doubles. Lyt attacks become phantom swipes — lower damage, cannot miss.",
      durationSec: 8,
      counterplay:
        "Holy-affinity enemies, Anti-Ghost elites, and Holy-ilk Tome wielders still strike the Tiger. The Spectral Tiger's greatest weakness is always light itself.",
    },
    kingsGrace: {
      id: "nine_lives",
      name: "Nine Lives",
      effect:
        "On fatal damage, the Tiger dissolves and reforms at the nearest shadow with 60% Anima, invisible to enemies for 3 seconds. Once per village visit.",
      restGated: true,
      // ⚑ FOLDED IN by THE CUT (Power Doctrine): st_k2 "Fuller
      // Grace" (50% → 75%) was a pure dial, landed at 60% on the
      // base; st_k3 "Shadowed Return" gave 3s of invisibility on
      // trigger and should always have been part of the base —
      // a panic button that drops you back into the exact spot
      // that just killed you is not a panic button.
      reformAnimaFraction: 0.6, // pool fraction — legally a percentage
      reformInvisibleSec: 3,
    },
    emperium: {
      id: "the_ghost_realm",
      // ⚑ THE EMPERIUM LAW clause 4 (Power Doctrine): THE WORLD
      // STAYS CHANGED. An Emperium is a decree upon the world,
      // not an ability — so when it ends it leaves a mark that
      // persists to the end of the engagement. A decal and a
      // material swap in fold3dKit; it turns the slot from a big
      // number into A THING THAT HAPPENED HERE.
      worldMark: { id: "monochrome", note: "The colour does not come all the way back. Enemies caught in the residue deal less damage until the engagement ends." },
      name: "The Ghost Realm",
      effect:
        "The battlefield becomes a monochrome shadow dimension. All enemies in radius are dragged along. For 5 seconds the Tiger has infinite Lyt attack speed and enemies cannot retaliate. The world snaps back — whatever died, died. Whatever survived, remembers.",
      durationSec: 5,
      radiusMeters: 25,
      restGated: true,
    },
  },

  chaos_dragon: {
    id: "chaos_dragon",
    lineage: "Chaos Dragon",
    lordName: "Xerath'oun",
    ilk: "Dragon",
    beingClass: "Dragon",
    archetype: "Berserker",
    oneLiner: "The more violence, the stronger you get.",
    epigraph:
      "Peace is for beings who fear. The Chaos Dragon fears nothing, and therefore knows only war.",
    lytAttack: {
      id: "dragons_bite",
      name: "Dragon's Bite",
      effect:
        "A heavy, slow, high-damage bite. Every hit builds a Chaos stack. At 10 Chaos stacks, the next Lyt unleashes a cone of breath weapon.",
      hits: 1,
      stackMechanic: { id: "chaos_stack", threshold: 10 },
    },
    lines: [
      {
        id: "disaster_path",
        name: "The Disaster Path",
        primera: {
          id: "natural_disaster",
          name: "Natural Disaster",
          effect:
            "Roar outward — enemies within close range are staggered and knocked back. Each staggered enemy grants +1 Chaos stack.",
        },
        impera: {
          id: "thriving_in_chaos",
          name: "Thriving in Chaos",
          effect:
            "For 6 seconds, every Lyt attack (by you or at you) deals 1 bonus damage to all enemies on screen. Punishes enemies for existing around you.",
          durationSec: 6,
        },
        uhlti: {
          id: "i_or_none",
          name: "I or None",
          effect:
            "Everyone on screen — you and all enemies — takes damage equal to 10% current Anima, every second, for 5 seconds. Last one standing wins. (Mechanically an Anima-cost move.)",
          durationSec: 5,
          animaCost: true,
        },
      },
    ],
    transformation: {
      id: "scaled_fury",
      name: "Scaled Fury",
      effect:
        "The Dragon grows in size. Lyf +2. Lyt attacks become AoE. Movement speed halved. A walking siege weapon.",
      durationSec: 10,
      // ⚑ GATE-1 (THE SMALL NUMBER LAW): was "+50% Lyf". The
      // cards give the Chaos Dragon Lyf 0 at EVERY tier, and
      // 50% of 0 is 0 — the node granted literally nothing.
      // A flat +2 grants it armor it has never had in its life.
      // Movement speed halved is spatial, not a stat magnitude,
      // so it stays a percentage. DIAL: lyfGrant.
      lyfGrant: 2,
    },
    kingsGrace: {
      id: "none_more_than_i",
      name: "None More Than I",
      effect:
        "For 8 seconds, enemies with a higher Attack stat than yours are instantly ruined on touch. Once per village visit. The Dragon tolerates no rivals.",
      durationSec: 8,
      restGated: true,
    },
    emperium: {
      id: "the_scorched_crater",
      // ⚑ THE EMPERIUM LAW clause 4 (Power Doctrine): THE WORLD
      // STAYS CHANGED. An Emperium is a decree upon the world,
      // not an ability — so when it ends it leaves a mark that
      // persists to the end of the engagement. A decal and a
      // material swap in fold3dKit; it turns the slot from a big
      // number into A THING THAT HAPPENED HERE.
      worldMark: { id: "scorched", note: "The fissures stay open and glowing. The ground is hot for the rest of the engagement." },
      name: "The Scorched Crater",
      effect:
        "The ground cracks open in a 40-tile radius. Magma bursts from the fissures. For 5 seconds all enemies in radius burn continuously, and the Dragon gains damage reduction equal to the number of enemies burning.",
      durationSec: 5,
      radiusTiles: 40,
      restGated: true,
    },
  },

  z4z: {
    id: "z4z",
    lineage: "Z4Z",
    lordName: "Quan-7",
    ilk: "Time", // Machine (Time)
    beingClass: "Machine",
    archetype: "Swarm Commander",
    oneLiner: "Every ally on the field makes you better.",
    epigraph: "The knight does not fight alone. The knight fights as a thousand.",
    lytAttack: {
      id: "blade_cycle",
      name: "Blade Cycle",
      effect:
        "A fast three-hit sword combo. Each hit has a 20% chance to spawn a tiny clockwork scarab that fights for 5 seconds. Scarabs count as allies for all Z4Z scaling effects.",
      hits: 3,
      procChance: 0.2,
    },
    lines: [
      {
        id: "apocalypse_path",
        name: "The Apocalypse Path",
        primera: {
          id: "summon_demon",
          name: "Summon Demon",
          effect:
            "Summon a Z4Z-Demon for 10 seconds. It has Relentless — pay 1 Anima-equivalent to let it Lyt attack again on kill.",
          durationSec: 10,
        },
        impera: {
          id: "summon_catastrophe",
          name: "Summon Catastrophe",
          effect:
            "Summon a Z4Z-Catastrophe for 12 seconds. If another time being is already in play, both gain extra attacks.",
          durationSec: 12,
        },
        uhlti: {
          id: "apocalypse_now",
          name: "Apocalypse Now",
          effect:
            "All current summons are replaced with a Z4Z-Apocalypse. It gains +1 Attack for every time being that has existed on this screen in the last 30 seconds. Lasts 8 seconds.",
          durationSec: 8,
        },
      },
    ],
    transformation: {
      id: "knights_of_chao",
      name: "Knights of Chao",
      effect:
        "Z4Z splits into four Z4Z-Wizards, each player-controllable via the skill bar. Each has 25% of your Anima. Your skills trigger from all four simultaneously. Glass-cannon commander mode.",
      durationSec: 8,
    },
    kingsGrace: {
      id: "blood_in_blood_out",
      name: "Blood in Blood Out",
      effect:
        "Consume all current summons. For each one consumed, summon a higher-tier replacement (Demon → Catastrophe → Apocalypse). Once per village visit. Rewards a deep field.",
      restGated: true,
    },
    emperium: {
      id: "the_clockwork_collapse",
      // ⚑ THE EMPERIUM LAW clause 4 (Power Doctrine): THE WORLD
      // STAYS CHANGED. An Emperium is a decree upon the world,
      // not an ability — so when it ends it leaves a mark that
      // persists to the end of the engagement. A decal and a
      // material swap in fold3dKit; it turns the slot from a big
      // number into A THING THAT HAPPENED HERE.
      worldMark: { id: "frozen_debris", note: "Debris hangs where time let go of it, never quite falling, for the rest of the engagement." },
      name: "The Clockwork Collapse",
      effect:
        "Time freezes for all enemies in a 50-tile radius. Z4Z and its summons act at normal speed for 5 seconds. Every hit lands. Every skill resets on end.",
      durationSec: 5,
      radiusTiles: 50,
      restGated: true,
    },
  },

  xulan: {
    id: "xulan",
    lineage: "Xulan",
    lordName: "Os'orun-Veth",
    ilk: "Space", // Humanoid (Space)
    beingClass: "Humanoid",
    archetype: "Mystic",
    oneLiner: "Defend, nullify, punish the aggressor.",
    epigraph: "The body is a vessel. The vessel is borrowed. Xulan remembers the lender.",
    lytAttack: {
      id: "psychic_lance",
      name: "Psychic Lance",
      effect:
        "A ranged projectile. Lower damage than melee Lyts, but Xulan stays at range. 10% chance to inflict Vessel Mark — marked enemies take +20% damage from Xulan's other abilities (mark fades after 8 seconds or is consumed on proc).",
      hits: 1,
      ranged: true,
      procChance: 0.1,
      procStatusId: "vessel_mark", // combat.js STATUS_EFFECTS registry
    },
    lines: [
      {
        id: "ghost_path",
        name: "The Ghost Path",
        primera: {
          id: "diamond_skin",
          name: "Diamond Skin",
          effect:
            "Lyf increases by 200% for 3 seconds. Completely negates one incoming attack and reflects it back at the attacker at 50% damage.",
          durationSec: 3,
        },
        impera: {
          id: "natural_scalpel",
          name: "Natural Scalpel",
          effect:
            "Fire five psychic scalpels at the five nearest enemies. Each scalpel deals bonus damage equal to that enemy's highest stat. Weakness-seeking.",
          scalpels: 5,
        },
        uhlti: {
          id: "sweet_dream",
          name: "Sweet Dream",
          effect:
            "All enemies that attacked Xulan in the last 5 seconds fall into a dream state. They take double damage while dreaming. Lasts 6 seconds.",
          durationSec: 6,
          appliesStatusId: "dream",
        },
      },
    ],
    transformation: {
      id: "xulan_ghost",
      name: "Xulan-Ghost",
      effect:
        "Xulan ascends; the body disappears. Cannot be directly targeted for 10 seconds. All Lyt attacks become AoE psychic bursts. Primera/Impera unusable during the form — Lyts only.",
      durationSec: 10,
    },
    kingsGrace: {
      id: "eternal_nightmare",
      name: "Eternal Nightmare",
      effect:
        "Select one enemy. For 12 seconds it cannot heal, cannot be healed, and deals 50% less damage to Xulan. Single-target boss-killer. Once per village visit.",
      durationSec: 12, // rebalanced from 30s in Build Guide v2 changelog
      restGated: true,
    },
    emperium: {
      id: "the_dream_sequence",
      // ⚑ THE EMPERIUM LAW clause 4 (Power Doctrine): THE WORLD
      // STAYS CHANGED. An Emperium is a decree upon the world,
      // not an ability — so when it ends it leaves a mark that
      // persists to the end of the engagement. A decal and a
      // material swap in fold3dKit; it turns the slot from a big
      // number into A THING THAT HAPPENED HERE.
      worldMark: { id: "dream_residue", note: "The air keeps its dream-sheen. Enemies entering the residue are briefly disoriented, for the rest of the engagement." },
      name: "The Dream Sequence",
      effect:
        "The world becomes a dream. All enemies in radius are yanked into a shared hallucination for 5 seconds — they cannot act and take periodic psychic damage. On waking, they are stunned for 2 further seconds.",
      durationSec: 5,
      restGated: true,
    },
  },
};

export const BEING_IDS = Object.keys(BEINGS);
export const getBeing = (id) => BEINGS[id] || null;
